import React from 'react';
import { Target, Zap, BarChart2, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface StatsSummaryCardProps {
  keywordCoverage: number;
  skillsMatchPercent: number;
  experienceAlignment: number;
  strengthCount: number;
  weakAreaCount: number;
  missingSkillsCount: number;
}

function MiniProgressBar({ value, color }: { value: number; color: string }) {
  return (
    <div data-component-id="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx:17:4" data-component-path="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx" data-component-line="17" data-component-end-line="22" data-component-file="StatsSummaryCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20bg-zinc-800%20rounded-full%20h-1.5%20mt-1.5%22%7D" className="w-full bg-zinc-800 rounded-full h-1.5 mt-1.5">
      <div data-component-id="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx:18:6" data-component-path="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx" data-component-line="18" data-component-end-line="21" data-component-file="StatsSummaryCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-1.5%20rounded-full%20transition-all%20duration-1000%20ease-out%22%7D"
        className="h-1.5 rounded-full transition-all duration-1000 ease-out"
        style={{ width: `${value}%`, backgroundColor: color }}
      />
    </div>
  );
}

const progressColor = (val: number) => {
  if (val >= 75) return '#10b981';
  if (val >= 50) return '#f59e0b';
  return '#f43f5e';
};

export default function StatsSummaryCard({
  keywordCoverage,
  skillsMatchPercent,
  experienceAlignment,
  strengthCount,
  weakAreaCount,
  missingSkillsCount,
}: StatsSummaryCardProps) {
  const stats = [
    {
      id: 'keyword-coverage',
      icon: Target,
      label: 'Keyword Coverage',
      value: `${keywordCoverage}%`,
      raw: keywordCoverage,
      desc: 'of JD keywords found in resume',
      showBar: true,
    },
    {
      id: 'skills-match',
      icon: Zap,
      label: 'Skills Match',
      value: `${skillsMatchPercent}%`,
      raw: skillsMatchPercent,
      desc: 'required skills present',
      showBar: true,
    },
    {
      id: 'experience-align',
      icon: BarChart2,
      label: 'Experience Alignment',
      value: `${experienceAlignment}%`,
      raw: experienceAlignment,
      desc: 'role-level and tenure match',
      showBar: true,
    },
    {
      id: 'strengths',
      icon: CheckCircle,
      label: 'Strengths Found',
      value: strengthCount.toString(),
      raw: null,
      desc: 'strong alignment points',
      showBar: false,
      valueColor: 'text-emerald-400',
    },
    {
      id: 'weak-areas',
      icon: AlertCircle,
      label: 'Weak Areas',
      value: weakAreaCount.toString(),
      raw: null,
      desc: 'areas needing improvement',
      showBar: false,
      valueColor: 'text-amber-400',
    },
    {
      id: 'missing-skills',
      icon: XCircle,
      label: 'Missing Keywords',
      value: missingSkillsCount.toString(),
      raw: null,
      desc: 'from job description',
      showBar: false,
      valueColor: 'text-rose-400',
    },
  ];

  return (
    <div data-component-id="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx:101:4" data-component-path="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx" data-component-line="101" data-component-end-line="133" data-component-file="StatsSummaryCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22card-glow%20rounded-2xl%20bg-zinc-900%20border%20border-zinc-800%20p-6%20h-full%20flex%20flex-col%20gap-4%22%7D" className="card-glow rounded-2xl bg-zinc-900 border border-zinc-800 p-6 h-full flex flex-col gap-4">
      <div data-component-id="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx:102:6" data-component-path="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx" data-component-line="102" data-component-end-line="105" data-component-file="StatsSummaryCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
        <BarChart2 data-component-id="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx:103:8" data-component-path="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx" data-component-line="103" data-component-end-line="103" data-component-file="StatsSummaryCard.tsx" data-component-name="BarChart2" data-component-content="%7B%22elementName%22%3A%22BarChart2%22%2C%22className%22%3A%22text-indigo-400%22%7D" size={15} className="text-indigo-400" />
        <span data-component-id="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx:104:8" data-component-path="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx" data-component-line="104" data-component-end-line="104" data-component-file="StatsSummaryCard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-semibold%20text-zinc-400%20uppercase%20tracking-wider%22%2C%22textContent%22%3A%22Analysis%20Breakdown%22%7D" className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Analysis Breakdown</span>
      </div>

      <div data-component-id="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx:107:6" data-component-path="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx" data-component-line="107" data-component-end-line="132" data-component-file="StatsSummaryCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-2%20sm%3Agrid-cols-3%20gap-3%20flex-1%22%7D" className="grid grid-cols-2 sm:grid-cols-3 gap-3 flex-1">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div data-component-id="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx:111:12" data-component-path="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx" data-component-line="111" data-component-end-line="129" data-component-file="StatsSummaryCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-xl%20bg-zinc-800%2F50%20border%20border-zinc-700%2F40%20p-3.5%20flex%20flex-col%20gap-1%22%7D"
              key={`stat-${stat.id}`}
              className="rounded-xl bg-zinc-800/50 border border-zinc-700/40 p-3.5 flex flex-col gap-1"
            >
              <div data-component-id="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx:115:14" data-component-path="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx" data-component-line="115" data-component-end-line="118" data-component-file="StatsSummaryCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-1.5%20mb-0.5%22%7D" className="flex items-center gap-1.5 mb-0.5">
                <Icon data-component-id="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx:116:16" data-component-path="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx" data-component-line="116" data-component-end-line="116" data-component-file="StatsSummaryCard.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-zinc-500%22%7D" size={12} className="text-zinc-500" />
                <span data-component-id="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx:117:16" data-component-path="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx" data-component-line="117" data-component-end-line="117" data-component-file="StatsSummaryCard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-zinc-500%20font-medium%20leading-tight%22%7D" className="text-xs text-zinc-500 font-medium leading-tight">{stat.label}</span>
              </div>
              <span data-component-id="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx:119:14" data-component-path="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx" data-component-line="119" data-component-end-line="124" data-component-file="StatsSummaryCard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                className={`text-2xl font-extrabold font-tabular leading-none ${stat.valueColor ?? ''}`}
                style={!stat.valueColor && stat.raw !== null ? { color: progressColor(stat.raw) } : undefined}
              >
                {stat.value}
              </span>
              <span data-component-id="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx:125:14" data-component-path="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx" data-component-line="125" data-component-end-line="125" data-component-file="StatsSummaryCard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-zinc-600%20leading-tight%22%7D" className="text-xs text-zinc-600 leading-tight">{stat.desc}</span>
              {stat.showBar && stat.raw !== null && (
                <MiniProgressBar data-component-id="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx:127:16" data-component-path="src/app/resume-analysis-tool/components/results/StatsSummaryCard.tsx" data-component-line="127" data-component-end-line="127" data-component-file="StatsSummaryCard.tsx" data-component-name="MiniProgressBar" data-component-content="%7B%22elementName%22%3A%22MiniProgressBar%22%7D" value={stat.raw} color={progressColor(stat.raw)} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}